/**
 * The board's spine: every workload in the estate with its live status.
 *
 * One Azure Resource Graph query produces this whole table — including VM power
 * state, which rides along in the extended instance view rather than needing a
 * per-VM call. Rows are ordered worst-first, so the stopped App Services lead
 * instead of hiding on page three of an alphabetical list.
 */

import { Icon, Search } from "@norican/icons";
import { Alert, Badge, DataGrid, EmptyState, Input, Toggle, ToggleGroup } from "@norican/react";
import { useMemo, useState } from "react";

import type { CloudWorkload, SubscriptionProblem } from "@/lib/api";

import { PanelCard } from "./panel-card";
import { bySeverity, shortType, STATE_LABEL, STATE_TONE, type WorkloadState } from "./status";

/** The status filters offered above the table. */
const FILTERS = [
  { value: "all", label: "All" },
  { value: "running", label: "Running" },
  { value: "stopped", label: "Stopped" },
  { value: "attention", label: "Needs attention" },
] as const;

type FilterValue = (typeof FILTERS)[number]["value"];

interface WorkloadTableProps {
  workloads: CloudWorkload[];
  /** Subscriptions missing from the list — rendered so the table is never silently short. */
  unreachable: SubscriptionProblem[];
}

export function WorkloadTable({ workloads, unreachable }: WorkloadTableProps) {
  const [filter, setFilter] = useState<FilterValue>("all");
  const [search, setSearch] = useState("");

  const rows = useMemo(() => {
    const matches = (state: WorkloadState) => {
      if (filter === "all") {
        return true;
      }
      if (filter === "attention") {
        return state !== "running";
      }
      return state === filter;
    };
    return workloads.filter((workload) => matches(workload.state)).sort(bySeverity);
  }, [workloads, filter]);

  const attention = workloads.filter((workload) => workload.state !== "running").length;

  return (
    <PanelCard
      title="Workload status"
      caption={
        attention > 0
          ? `${workloads.length} workloads · ${attention} not running`
          : `${workloads.length} workloads · all running`
      }
      padding="none"
    >
      <div className="flex flex-wrap items-center gap-3 px-4 pt-4 pb-3">
        <ToggleGroup.Root
          appearance="segmented"
          size="sm"
          aria-label="Filter workloads by status"
          value={[filter]}
          onValueChange={(next) => {
            const chosen = next[0];
            if (chosen) {
              setFilter(chosen as FilterValue);
            }
          }}
        >
          {FILTERS.map((option) => (
            <Toggle key={option.value} value={option.value}>
              {option.label}
            </Toggle>
          ))}
        </ToggleGroup.Root>

        <Input
          size="sm"
          value={search}
          onValueChange={setSearch}
          placeholder="Filter by name, type, subscription…"
          startSlot={<Icon icon={Search} size="sm" />}
          className="min-w-64 flex-1"
          aria-label="Filter workloads"
        />
      </div>

      {unreachable.length > 0 && (
        <div className="px-4 pb-3">
          <Alert tone="danger" title="This table is incomplete">
            {unreachable.length} subscription{unreachable.length === 1 ? "" : "s"} could not be read
            ({unreachable.map((problem) => problem.name).join(", ")}), so any workload in{" "}
            {unreachable.length === 1 ? "it" : "them"} is missing from this list.
          </Alert>
        </div>
      )}

      {rows.length === 0 ? (
        <div className="px-4 pb-6">
          <EmptyState
            size="sm"
            title="No workloads match this filter"
            description="Clear the status filter or the search term to see the full estate."
          />
        </div>
      ) : (
        <DataGrid<CloudWorkload>
          data={rows}
          getRowId={(workload) => workload.id}
          getRowSearchText={(workload) =>
            `${workload.name} ${workload.kind} ${workload.type} ${workload.subscription_name} ${workload.resource_group} ${workload.location} ${workload.status}`
          }
          globalFilter={search}
          density="comfortable"
          aria-label="Azure workloads"
          columns={[
            {
              id: "name",
              header: "Workload",
              accessor: (workload) => workload.name,
              sortable: true,
              cell: (workload) => (
                <div className="flex flex-col">
                  <span className="text-fg font-medium">{workload.name}</span>
                  <span className="text-fg-muted text-xs">{shortType(workload.type)}</span>
                </div>
              ),
            },
            {
              id: "kind",
              header: "Type",
              accessor: (workload) => workload.kind,
              sortable: true,
              cell: (workload) => <span className="text-fg-muted text-sm">{workload.kind}</span>,
            },
            {
              id: "state",
              header: "Status",
              accessor: (workload) => workload.state,
              sortable: true,
              cell: (workload) => (
                <div className="flex flex-col items-start gap-0.5">
                  <Badge tone={STATE_TONE[workload.state]} variant="subtle" size="sm">
                    {STATE_LABEL[workload.state]}
                  </Badge>
                  {/* Show what Azure actually said, not just our normalisation. */}
                  {workload.status && workload.status !== STATE_LABEL[workload.state] && (
                    <span className="text-fg-subtle text-xs">{workload.status}</span>
                  )}
                </div>
              ),
            },
            {
              id: "subscription",
              header: "Subscription",
              accessor: (workload) => workload.subscription_name,
              sortable: true,
              cell: (workload) => (
                <span className="text-fg-muted text-sm">{workload.subscription_name}</span>
              ),
            },
            {
              id: "resource_group",
              header: "Resource group",
              accessor: (workload) => workload.resource_group,
              sortable: true,
              cell: (workload) => (
                <span className="text-fg-muted text-sm">{workload.resource_group}</span>
              ),
            },
            {
              id: "location",
              header: "Region",
              accessor: (workload) => workload.location,
              sortable: true,
              cell: (workload) => (
                <span className="text-fg-muted text-sm">{workload.location}</span>
              ),
            },
          ]}
        />
      )}
    </PanelCard>
  );
}
